import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/** Gemini Luminous-style hover tooltip (inverse surface pill). */
const HoverTooltip = ({
  label,
  children,
  /** 'right' | 'bottom' — sidebar toggles use right (next to icon). */
  placement = 'right',
  delayMs = 400,
  offsetPx = 8,
}) => {
  const triggerRef = useRef(null);
  const showTimerRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState(null);

  const clearShowTimer = () => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
  };

  const updatePosition = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;

    if (placement === 'bottom') {
      setCoords({
        top: rect.bottom + offsetPx,
        left: rect.left + rect.width / 2,
        transform: 'translateX(-50%)',
      });
      return;
    }

    // Default: to the right of the icon, vertically centered
    setCoords({
      top: rect.top + rect.height / 2,
      left: rect.right + offsetPx,
      transform: 'translateY(-50%)',
    });
  };

  const show = () => {
    clearShowTimer();
    showTimerRef.current = setTimeout(() => {
      updatePosition();
      setVisible(true);
    }, delayMs);
  };

  const hide = () => {
    clearShowTimer();
    setVisible(false);
  };

  useEffect(() => () => clearShowTimer(), []);

  useEffect(() => {
    if (!visible) return undefined;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [visible, offsetPx, placement]);

  return (
    <>
      <span
        ref={triggerRef}
        className="inline-flex"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {children}
      </span>
      {visible &&
        coords &&
        createPortal(
          <div
            role="tooltip"
            className="pointer-events-none fixed z-[100] whitespace-nowrap rounded-[12px] px-4 py-2 text-xs font-normal leading-4 tracking-[0.006rem] bg-[#303030] text-[#e3e3e3] shadow-none dark:bg-[#e0e0e0] dark:text-[#141414]"
            style={{
              top: coords.top,
              left: coords.left,
              transform: coords.transform,
            }}
          >
            {label}
          </div>,
          document.body,
        )}
    </>
  );
};

export default HoverTooltip;
